/// <amd-module name="Agile/Sprints/Directory/SprintDirectoryContentView" />
import { ITeam } from "Agile/Contracts/Common";
import {
    Actions as DefaultActions,
    DirectoryContentView,
    IDirectoryContentViewMappedProps,
    IDirectoryContentViewOwnProps,
    mapStateToProps as defaultMapStateToProps
} from "Agile/Directory/Components/DirectoryContentView";
import {
    DirectoryFilterBar,
    IDirectoryFilterBarOwnProps,
    IDirectoryFilterBarProps
} from "Agile/Directory/Components/DirectoryFilterBar/DirectoryFilterBar";
import { DEFAULT_NAME_COLUMN, DEFAULT_TEAM_COLUMN, IDirectoryRow } from "Agile/Directory/Components/DirectoryGrid";
import { IBackcompatIteration } from "Agile/Sprints/Dialog/Contracts";
import { SprintPaneWrapper } from "Agile/Sprints/Dialog/SprintPaneWrapper";
import { DateCell } from "Agile/Sprints/Directory/DateCell";
import { CurrentIterationHeader, DatesColumnHeader, NewSprint, PageNotice } from "Agile/Sprints/Directory/Resources";
import { ColumnActionsMode, IColumn } from "OfficeFabric/DetailsList";
import * as React from "react";
import { connect } from "react-redux";
import { SprintDirectoryActions } from "Redux/SprintDirectoryActions";
import { areSprintsPaged, getEditSprintDetails, getSprintOwners, isSprintPaneOpen } from "Redux/SprintsDirectorySelectors";
import { ISprintDirectoryArtifact, ISprintDirectoryViewState } from "SprintDirectoryContracts";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { IFilter } from "VSSUI/Utilities/Filter";

const ITERATION_NAME_COLUMN: IColumn = {
    fieldName: "IterationName",
    key: "IterationName",
    name: CurrentIterationHeader,
    minWidth: 200,
    maxWidth: 400,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled
};

const ITERATION_DATE_COLUMN: IColumn = {
    fieldName: "IterationDate",
    key: "IterationDate",
    name: DatesColumnHeader,
    minWidth: 200,
    maxWidth: 400,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled
};
export interface ISprintDirectoryContentViewMappedProps extends IDirectoryContentViewMappedProps<ISprintDirectoryArtifact> {
    /** Indicates if sprints being truncated */
    areSprintsPaged?: boolean;
    /** The currently edited sprint details */
    editSprintDetails?: {
        iterationId: string;
        team: { id: string; name: string };
    };
    /** Indicates if the sprint pane is open */
    isSprintPaneOpen: boolean;
}

export interface ISprintDirectoryContentViewProps
    extends IDirectoryContentViewOwnProps<ISprintDirectoryArtifact>,
        ISprintDirectoryContentViewMappedProps {}

class SprintDirectoryContentView extends React.Component<ISprintDirectoryContentViewProps & typeof SprintActions> {
    public render(): JSX.Element {
        return (
            <>
                <DirectoryContentView
                    {...this.props}
                    commandBarItems={[
                        {
                            id: "new-sprint",
                            text: NewSprint,
                            ariaLabel: NewSprint,
                            isPrimary: true,
                            onActivate: this._onNewSprintClicked
                        }
                    ]}
                    customColumns={[DEFAULT_NAME_COLUMN, DEFAULT_TEAM_COLUMN, ITERATION_NAME_COLUMN, ITERATION_DATE_COLUMN]}
                    onRenderCell={this._onRenderCell}
                    onRenderFilterBar={this._onRenderFilterBar}
                    onRenderMessageArea={this._onRenderPagedMessage}
                />
                {this._renderSprintPane()}
            </>
        );
    }

    private _renderSprintPane(): JSX.Element | undefined {
        const { isSprintPaneOpen, editSprintDetails } = this.props;

        if (isSprintPaneOpen) {
            return (
                <SprintPaneWrapper
                    onCompleted={editSprintDetails ? this._onSprintEdited : this._onSprintCreated}
                    onDismiss={this._onSprintPaneDismissed}
                    currentTeam={editSprintDetails ? editSprintDetails.team : undefined}
                    editingIterationId={editSprintDetails ? editSprintDetails.iterationId : undefined}
                />
            );
        }
    }

    private _onRenderCell = (
        item: IDirectoryRow<ISprintDirectoryArtifact>,
        index: number,
        column: IColumn,
        defaultRenderer: (item: IDirectoryRow<ISprintDirectoryArtifact>, index: number, column: IColumn) => JSX.Element
    ): JSX.Element => {
        if (column.key === ITERATION_NAME_COLUMN.key) {
            return <div>{item.data.iterationName}</div>;
        } else if (column.key === ITERATION_DATE_COLUMN.key) {
            return <DateCell row={item} onClickSetDates={this._onSetIterationDatesClicked} />;
        }

        return defaultRenderer(item, index, column);
    };

    private _onRenderFilterBar = (filter: IFilter): JSX.Element => {
        return <SprintConnectedDirectoryFilterBar filter={filter} />;
    };

    private _onRenderPagedMessage = (): JSX.Element | undefined => {
        const { areSprintsPaged } = this.props;

        if (areSprintsPaged) {
            return <MessageBar severity={MessageBarSeverity.Warning}>{PageNotice}</MessageBar>;
        }
    };

    private _onNewSprintClicked = (): void => {
        this.props.openNewSprint();
    };

    private _onSetIterationDatesClicked = (artifact: ISprintDirectoryArtifact): void => {
        this.props.editSprint({ id: artifact.id, name: artifact.name }, artifact.iterationId);
    };

    private _onSprintCreated = (team: ITeam, iteration: IBackcompatIteration): void => {
        this.props.newSprintSucceeded(team, iteration);
    };

    private _onSprintEdited = (team: ITeam, iteration: IBackcompatIteration): void => {
        this.props.editSprintSucceeded(team, iteration);
    };

    private _onSprintPaneDismissed = (): void => {
        this.props.closeSprintPane();
    };
}

export function mapStateToFilterProps(state: ISprintDirectoryViewState, ownProps: IDirectoryFilterBarOwnProps): IDirectoryFilterBarProps {
    return {
        ...ownProps,
        owners: getSprintOwners(state)
    };
}

const SprintConnectedDirectoryFilterBar = connect(
    mapStateToFilterProps,
    null
)(DirectoryFilterBar);

export function mapStateToProps(): (
    state: ISprintDirectoryViewState,
    ownProps: IDirectoryContentViewOwnProps<ISprintDirectoryArtifact>
) => ISprintDirectoryContentViewMappedProps {
    const baseSelector = defaultMapStateToProps<ISprintDirectoryArtifact>();
    return (state: ISprintDirectoryViewState, ownProps: IDirectoryContentViewOwnProps<ISprintDirectoryArtifact>) => {
        return {
            ...baseSelector(state, ownProps),
            areSprintsPaged: areSprintsPaged(state),
            editSprintDetails: getEditSprintDetails(state),
            isSprintPaneOpen: isSprintPaneOpen(state)
        };
    };
}

export const SprintActions = {
    ...DefaultActions,
    openNewSprint: SprintDirectoryActions.newSprintOpened,
    newSprintSucceeded: SprintDirectoryActions.newSprintSucceeded,
    editSprint: SprintDirectoryActions.editSprintOpened,
    editSprintSucceeded: SprintDirectoryActions.editSprintSucceeded,
    closeSprintPane: SprintDirectoryActions.sprintPaneClosed
};

export const ConnectedSprintDirectoryContentView = connect(
    mapStateToProps,
    SprintActions
)(SprintDirectoryContentView);
